export interface Role {
  id: string;
  name: string;
  description?: string;
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName?: string;
  phone?: string;
  isActive: boolean;
  lastLoginAt?: string;
  mfaEnabled: boolean;
  emailVerified: boolean;
  roles: Role[];
  permissions: string[];
  moduleAccess: string[];
  createdAt?: string;
  updatedAt?: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
  mfaCode?: string;
  rememberMe?: boolean;
}

export interface LoginResponse {
  user: User;
  tokens: {
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
  };
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: ApiError;
  meta?: {
    timestamp: string;
    requestId: string;
  };
}

/**
 * Available user roles in the system
 * @see USER_ROLES.md for detailed role definitions
 */
export type UserRole =
  | 'SUPER_ADMIN'
  | 'ADMIN'
  | 'SECURITY_MANAGER'
  | 'ANALYST'
  | 'OPERATOR'
  | 'VIEWER'
  | 'LEGAL_ADMIN';

/**
 * Permission resource types
 */
export type PermissionResource =
  | 'users'
  | 'incidents'
  | 'threats'
  | 'executive_protection'
  | 'investigations'
  | 'security_vendors'
  | 'analytics'
  | 'notifications'
  | 'audit_logs'
  | 'system'
  | 'api_keys'
  | 'hipaa_incidents'
  | 'groups'
  | 'templates'
  | 'lifecycle_steps'
  | 'due_date';

/**
 * Permission action types
 */
export type PermissionAction =
  | 'create'
  | 'read'
  | 'update'
  | 'delete'
  | 'manage'
  | 'assign'
  | 'assign_roles'
  | 'close'
  | 'complete'
  | 'override'
  | 'export'
  | 'assess'
  | 'sources'
  | 'tracking'
  | 'geofence'
  | 'activities'
  | 'evidence'
  | 'reports'
  | 'advanced'
  | 'broadcast'
  | 'configure'
  | 'monitor'
  | 'review'
  | 'verify';

/**
 * Complete permission list following resource:action pattern
 * @see USER_ROLES.md for permission matrix
 */
export type UserPermission =
  // User Management Permissions
  | 'users:create'
  | 'users:read'
  | 'users:update'
  | 'users:delete'
  | 'users:assign_roles'
  // Incident Management Permissions
  | 'incidents:create'
  | 'incidents:read'
  | 'incidents:update'
  | 'incidents:delete'
  | 'incidents:assign'
  | 'incidents:approve-closure'
  | 'incidents:request-closure'
  | 'incidents:approve-any-closure'
  | 'incidents:reassign-supervisor'
  | 'incidents:export'
  // Threat Intelligence Permissions
  | 'threats:create'
  | 'threats:read'
  | 'threats:update'
  | 'threats:delete'
  | 'threats:assess'
  | 'threats:sources'
  | 'threats:export'
  // Executive Protection Permissions
  | 'executive_protection:create'
  | 'executive_protection:read'
  | 'executive_protection:update'
  | 'executive_protection:delete'
  | 'executive_protection:geofence'
  | 'executive_protection:activities'
  // Investigation Permissions
  | 'investigations:create'
  | 'investigations:read'
  | 'investigations:update'
  | 'investigations:delete'
  | 'investigations:assign'
  | 'investigations:close'
  | 'investigations:evidence'
  // Security Vendor Directory Permissions (VIS-199)
  | 'security_vendors:read'
  | 'security_vendors:create'
  | 'security_vendors:update'
  | 'security_vendors:delete'
  // Vendor Rating & Review Permissions (VIS-200)
  | 'security_vendors:review'
  | 'security_vendors:verify'
  // Analytics & Reporting Permissions
  | 'analytics:read'
  | 'analytics:reports'
  | 'analytics:export'
  | 'analytics:advanced'
  // Notification Permissions
  | 'notifications:read'
  | 'notifications:manage'
  | 'notifications:broadcast'
  // Audit & System Permissions
  | 'audit_logs:read'
  | 'audit_logs:export'
  | 'system:configure'
  | 'system:monitor'
  | 'api_keys:manage'
  // HIPAA Incident Tracking (45 CFR 164.400-414)
  | 'hipaa_incidents:read'
  | 'hipaa_incidents:create'
  | 'hipaa_incidents:update'
  | 'hipaa_incidents:delete'
  // Spec 3.10.9 Table 5 — HIPAA breach notification authority
  | 'groups:read'
  | 'groups:create'
  | 'groups:update'
  | 'groups:delete'
  | 'templates:read'
  | 'templates:create'
  | 'templates:update'
  | 'templates:delete'
  | 'lifecycle_steps:complete'
  | 'due_date:override'
  | 'hipaa_incidents:close'
  // Wildcard permission for SUPER_ADMIN
  | '*';

/**
 * Role permission mappings
 * Defines which permissions each role has by default
 */
export const ROLE_PERMISSIONS: Record<UserRole, UserPermission[]> = {
  SUPER_ADMIN: ['*'], // All permissions

  ADMIN: [
    'users:create',
    'users:read',
    'users:update',
    'users:assign_roles',
    'incidents:create',
    'incidents:read',
    'incidents:update',
    'incidents:delete',
    'incidents:assign',
    'incidents:approve-closure',
    'incidents:request-closure',
    'incidents:approve-any-closure',
    'incidents:reassign-supervisor',
    'incidents:export',
    'threats:create',
    'threats:read',
    'threats:update',
    'threats:delete',
    'threats:assess',
    'threats:sources',
    'threats:export',
    'executive_protection:create',
    'executive_protection:read',
    'executive_protection:update',
    'executive_protection:delete',
    'executive_protection:geofence',
    'executive_protection:activities',
    'investigations:create',
    'investigations:read',
    'investigations:update',
    'investigations:delete',
    'investigations:assign',
    'investigations:close',
    'investigations:evidence',
    'security_vendors:read',
    'security_vendors:create',
    'security_vendors:update',
    'security_vendors:delete',
    'security_vendors:review',
    'security_vendors:verify',
    'analytics:read',
    'analytics:reports',
    'analytics:export',
    'analytics:advanced',
    'notifications:read',
    'notifications:manage',
    'notifications:broadcast',
    'audit_logs:read',
    'audit_logs:export',
    'system:monitor',
  ],

  SECURITY_MANAGER: [
    'users:read',
    'incidents:create',
    'incidents:read',
    'incidents:update',
    'incidents:delete',
    'incidents:assign',
    'incidents:approve-closure',
    'incidents:request-closure',
    'incidents:reassign-supervisor',
    'incidents:export',
    'threats:create',
    'threats:read',
    'threats:update',
    'threats:delete',
    'threats:assess',
    'threats:sources',
    'threats:export',
    'executive_protection:create',
    'executive_protection:read',
    'executive_protection:update',
    'executive_protection:delete',
    'executive_protection:geofence',
    'executive_protection:activities',
    'investigations:create',
    'investigations:read',
    'investigations:update',
    'investigations:delete',
    'investigations:assign',
    'investigations:close',
    'investigations:evidence',
    'security_vendors:read',
    'security_vendors:create',
    'security_vendors:update',
    'security_vendors:review',
    'security_vendors:verify',
    'analytics:read',
    'analytics:reports',
    'analytics:export',
    'analytics:advanced',
    'notifications:read',
    'notifications:manage',
    'notifications:broadcast',
  ],

  ANALYST: [
    'incidents:create',
    'incidents:read',
    'incidents:update',
    'incidents:assign',
    'incidents:request-closure',
    'incidents:export',
    'threats:create',
    'threats:read',
    'threats:update',
    'threats:assess',
    'threats:export',
    'executive_protection:read',
    'executive_protection:update',
    'investigations:read',
    'investigations:update',
    'investigations:evidence',
    'security_vendors:read',
    'security_vendors:create',
    'security_vendors:review',
    'analytics:read',
    'analytics:export',
    'notifications:read',
  ],

  OPERATOR: [
    'incidents:create',
    'incidents:read',
    'incidents:update',
    'incidents:request-closure',
    'threats:read',
    'executive_protection:read',
    'investigations:read',
    'security_vendors:read',
    'security_vendors:create',
    'notifications:read',
  ],

  VIEWER: [
    'incidents:read',
    'threats:read',
    'executive_protection:read',
    'investigations:read',
    'security_vendors:read',
    'analytics:read',
    'notifications:read',
  ],

  LEGAL_ADMIN: [
    'users:read',
    'analytics:read',
    'notifications:read',
    'audit_logs:read',
    // HIPAA module access (gateway gates /api/v1/legal/hipaa/* on hipaa_incidents:read)
    'hipaa_incidents:read',
    'hipaa_incidents:create',
    'hipaa_incidents:update',
    'hipaa_incidents:delete',
    // Spec 3.10.9 Table 5 — HIPAA breach notification authority
    'groups:read',
    'groups:create',
    'groups:update',
    'groups:delete',
    'templates:read',
    'templates:create',
    'templates:update',
    'templates:delete',
    'lifecycle_steps:complete',
    'due_date:override',
    'hipaa_incidents:close',
  ],
};

/**
 * Helper type for permission checking
 */
export interface PermissionCheck {
  resource: PermissionResource;
  action: PermissionAction;
}

/**
 * Parse a permission string into its components
 */
export function parsePermission(permission: string): PermissionCheck | null {
  const parts = permission.split(':');
  if (parts.length !== 2) {return null;}
  return {
    resource: parts[0] as PermissionResource,
    action: parts[1] as PermissionAction,
  };
}

/**
 * Build a permission string from components
 */
export function buildPermission(resource: PermissionResource, action: PermissionAction): UserPermission {
  return `${resource}:${action}` as UserPermission;
}

/**
 * Check if a user has a specific permission
 */
export function hasPermission(userPermissions: string[], requiredPermission: UserPermission): boolean {
  // Check for wildcard permission
  if (userPermissions.includes('*')) {
    return true;
  }
  return userPermissions.includes(requiredPermission);
}

/**
 * Check if a user has any of the specified permissions
 */
export function hasAnyPermission(userPermissions: string[], requiredPermissions: UserPermission[]): boolean {
  if (userPermissions.includes('*')) {
    return true;
  }
  return requiredPermissions.some((permission) => userPermissions.includes(permission));
}

/**
 * Check if a user has all of the specified permissions
 */
export function hasAllPermissions(userPermissions: string[], requiredPermissions: UserPermission[]): boolean {
  if (userPermissions.includes('*')) {
    return true;
  }
  return requiredPermissions.every((permission) => userPermissions.includes(permission));
}

/**
 * Check if a user has a specific role
 */
export function hasRole(userRoles: Role[], requiredRole: UserRole): boolean {
  return userRoles.some((role) => role.name === requiredRole);
}

/**
 * Check if a user has any of the specified roles
 */
export function hasAnyRole(userRoles: Role[], requiredRoles: UserRole[]): boolean {
  return requiredRoles.some((requiredRole) => userRoles.some((role) => role.name === requiredRole));
}

/**
 * Check if a user has all of the specified roles
 */
export function hasAllRoles(userRoles: Role[], requiredRoles: UserRole[]): boolean {
  return requiredRoles.every((requiredRole) => userRoles.some((role) => role.name === requiredRole));
}

/**
 * Get permissions for a specific role
 */
export function getPermissionsForRole(role: UserRole): UserPermission[] {
  return ROLE_PERMISSIONS[role] ?? [];
}